import _extends from "@babel/runtime/helpers/esm/extends";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { createElement } from "@wordpress/element";
/**
 * External dependencies
 */

import classnames from 'classnames';
import { isFunction, noop, startsWith } from 'lodash';
/**
 * WordPress dependencies
 */

import { Button, ExternalLink, Popover } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useCallback, useState, useEffect, Fragment } from '@wordpress/element';
import { safeDecodeURI, filterURLForDisplay, isURL, prependHTTP, getProtocol } from '@wordpress/url';
import { withInstanceId, compose } from '@wordpress/compose';
import { withSelect } from '@wordpress/data';
/**
 * Internal dependencies
 */

import LinkControlSettingsDrawer from './settings-drawer';
import LinkControlSearchItem from './search-item';
import LinkControlSearchInput from './search-input';
var MODE_EDIT = 'edit'; // const MODE_SHOW = 'show';

function LinkControl(_ref) {
  var className = _ref.className,
      currentLink = _ref.currentLink,
      currentSettings = _ref.currentSettings,
      fetchSearchSuggestions = _ref.fetchSearchSuggestions,
      instanceId = _ref.instanceId,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? noop : _ref$onClose,
      _ref$onChangeMode = _ref.onChangeMode,
      onChangeMode = _ref$onChangeMode === void 0 ? noop : _ref$onChangeMode,
      _ref$onKeyDown = _ref.onKeyDown,
      onKeyDown = _ref$onKeyDown === void 0 ? noop : _ref$onKeyDown,
      _ref$onKeyPress = _ref.onKeyPress,
      onKeyPress = _ref$onKeyPress === void 0 ? noop : _ref$onKeyPress,
      _ref$onLinkChange = _ref.onLinkChange,
      onLinkChange = _ref$onLinkChange === void 0 ? noop : _ref$onLinkChange,
      _ref$onSettingsChange = _ref.onSettingsChange,
      onSettingsChange = _ref$onSettingsChange === void 0 ? noop : _ref$onSettingsChange; // State

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      inputValue = _useState2[0],
      setInputValue = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isEditingLink = _useState4[0],
      setIsEditingLink = _useState4[1]; // Effects


  useEffect(function () {
    // If we have a link then stop editing mode
    if (currentLink) {
      setIsEditingLink(false);
    } else {
      setIsEditingLink(true);
    }
  }, [currentLink]); // Handlers

  /**
   * onChange LinkControlSearchInput event handler
   *
   * @param {string} value Current value returned by the search.
   */

  var onInputChange = function onInputChange() {
    var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    setInputValue(value);
  }; // Utils

  /**
   * Handler function which switches the mode of the component,
   * between `edit` and `show` mode.
   * Also, it calls `onChangeMode` callback function.
   *
   * @param {string} mode Component mode: `show` or `edit`.
   */


  var setMode = function setMode() {
    var mode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'show';
    return function () {
      setIsEditingLink(MODE_EDIT === mode); // Populate input searcher whether
      // the current link has a title.

      if (currentLink && currentLink.title) {
        setInputValue(currentLink.title);
      }

      if (isFunction(onChangeMode)) {
        onChangeMode(mode);
      }
    };
  };

  var closeLinkUI = function closeLinkUI() {
    resetInput();
    onClose();
  };

  var resetInput = function resetInput() {
    setInputValue('');
  };

  var handleDirectEntry = function handleDirectEntry(value) {
    var type = 'URL';
    var protocol = getProtocol(value) || '';

    if (protocol.includes('mailto')) {
      type = 'mailto';
    }

    if (protocol.includes('tel')) {
      type = 'tel';
    }

    if (startsWith(value, '#')) {
      type = 'internal';
    }

    return Promise.resolve([{
      id: '-1',
      title: value,
      url: type === 'URL' ? prependHTTP(value) : value,
      type: type
    }]);
  };

  var handleEntitySearch =
  /*#__PURE__*/
  function () {
    var _ref2 = _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee(value) {
      var results, couldBeURL;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return Promise.all([fetchSearchSuggestions(value), handleDirectEntry(value)]);

            case 2:
              results = _context.sent;
              couldBeURL = !value.includes(' '); // If it's potentially a URL search then concat on a URL search suggestion
              // just for good measure. That way once the actual results run out we always
              // have a URL option to fallback on.

              return _context.abrupt("return", couldBeURL ? results[0].concat(results[1]) : results[0]);

            case 5:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function handleEntitySearch(_x) {
      return _ref2.apply(this, arguments);
    };
  }(); // Effects


  var getSearchHandler = useCallback(function (value) {
    var protocol = getProtocol(value) || '';
    var isMailto = protocol.includes('mailto');
    var isInternal = startsWith(value, '#');
    var isTel = protocol.includes('tel');
    var handleManualEntry = isInternal || isMailto || isTel || isURL(value) || value && value.includes('www.');
    return handleManualEntry ? handleDirectEntry(value) : handleEntitySearch(value);
  }, [handleDirectEntry, fetchSearchSuggestions]); // Render Components

  var renderSearchResults = function renderSearchResults(_ref3) {
    var suggestionsListProps = _ref3.suggestionsListProps,
        buildSuggestionItemProps = _ref3.buildSuggestionItemProps,
        suggestions = _ref3.suggestions,
        selectedSuggestion = _ref3.selectedSuggestion,
        isLoading = _ref3.isLoading;
    var resultsListClasses = classnames('block-editor-link-control__search-results', {
      'is-loading': isLoading
    });
    var manualLinkEntryTypes = ['url', 'mailto', 'tel', 'internal'];
    return createElement("div", {
      className: "block-editor-link-control__search-results-wrapper"
    }, createElement("div", _extends({}, suggestionsListProps, {
      className: resultsListClasses
    }), suggestions.map(function (suggestion, index) {
      return createElement(LinkControlSearchItem, {
        key: "".concat(suggestion.id, "-").concat(suggestion.type),
        itemProps: buildSuggestionItemProps(suggestion, index),
        suggestion: suggestion,
        onClick: function onClick() {
          return onLinkChange(suggestion);
        },
        isSelected: index === selectedSuggestion,
        isURL: manualLinkEntryTypes.includes(suggestion.type.toLowerCase()),
        searchTerm: inputValue
      });
    })));
  };

  return createElement(Popover, {
    className: classnames('block-editor-link-control', className),
    onClose: closeLinkUI,
    position: "bottom center",
    focusOnMount: "firstElement"
  }, createElement("div", {
    className: "block-editor-link-control__popover-inner"
  }, createElement("div", {
    className: "block-editor-link-control__search"
  }, !isEditingLink && currentLink && createElement(Fragment, null, createElement("p", {
    className: "screen-reader-text",
    id: "current-link-label-".concat(instanceId)
  }, __('Currently selected'), ":"), createElement("div", {
    "aria-labelledby": "current-link-label-".concat(instanceId),
    "aria-selected": "true",
    className: classnames('block-editor-link-control__search-item', {
      'is-current': true
    })
  }, createElement("span", {
    className: "block-editor-link-control__search-item-header"
  }, createElement(ExternalLink, {
    className: "block-editor-link-control__search-item-title",
    href: currentLink.url
  }, currentLink.title), createElement("span", {
    className: "block-editor-link-control__search-item-info"
  }, filterURLForDisplay(safeDecodeURI(currentLink.url)) || '')), createElement(Button, {
    isDefault: true,
    onClick: setMode(MODE_EDIT),
    className: "block-editor-link-control__search-item-action block-editor-link-control__search-item-action--edit"
  }, __('Change')))), isEditingLink && createElement(LinkControlSearchInput, {
    value: inputValue,
    onChange: onInputChange,
    onSelect: onLinkChange,
    renderSuggestions: renderSearchResults,
    fetchSuggestions: getSearchHandler,
    onReset: resetInput,
    onKeyDown: onKeyDown,
    onKeyPress: onKeyPress
  }), !isEditingLink && createElement(LinkControlSettingsDrawer, {
    settings: currentSettings,
    onSettingChange: onSettingsChange
  }))));
}

export default compose(withInstanceId, withSelect(function (select, ownProps) {
  if (ownProps.fetchSearchSuggestions && isFunction(ownProps.fetchSearchSuggestions)) {
    return;
  }

  var _select = select('core/block-editor'),
      getSettings = _select.getSettings;

  return {
    fetchSearchSuggestions: getSettings().__experimentalFetchLinkSuggestions
  };
}))(LinkControl);